import React, { useEffect, useState } from "react";
import {
    Table,
    Button,
    Input,
    Space,
    Switch,
    message,
    Tooltip,
    Popconfirm,
    Skeleton,
    Typography,
    Form,
    Segmented
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Currency } from "../types/currencytypes";
import {
    getCurrencies,
    updateCurrencyStatus,
    createCurrency,
    updateCurrency
} from "../services/currencyservice";
import CurrencyDrawer from "../components/currencies/currencydrawer";
import { useAuth } from "../auth/authcontext";

const { Title } = Typography;
const { Search } = Input;

type StatusFilter = "all" | "active" | "inactive";

const Currencies: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [currencies, setCurrencies] = useState<Currency[]>([]);

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] =
        useState<StatusFilter>("all");

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedCurrency, setSelectedCurrency] =
        useState<Currency | null>(null);

    const [form] = Form.useForm();

    const { userId, isReadOnly } = useAuth();
    const createdByGuid = userId || "";

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });

    const fetchCurrencies = async (
        page = 1,
        pageSize = pagination.pageSize,
        searchTerm = search,
        status = statusFilter
    ) => {
        setLoading(true);
        try {
            const offset = (page - 1) * pageSize;

            const result = await getCurrencies(
                searchTerm,
                status,
                pageSize,
                offset
            );

            setCurrencies(result.data);
            setPagination({
                current: page,
                pageSize,
                total: result.totalCount
            });
        } catch {
            message.error("Failed to load currencies");
        } finally {
            setLoading(false);
        }
    };

    // Initial load
    useEffect(() => {
        fetchCurrencies(1, pagination.pageSize, "", "all");
    }, []);

    // Re-fetch when status filter changes
    useEffect(() => {
        fetchCurrencies(1, pagination.pageSize, search, statusFilter);
    }, [statusFilter]);

    const handleSearch = (value: string) => {
        const trimmed = value.trim();
        setSearch(trimmed);
        fetchCurrencies(1, pagination.pageSize, trimmed, statusFilter);
    };

    const handleSearchChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (!e.target.value) {
            setSearch("");
            fetchCurrencies(1, pagination.pageSize, "", statusFilter);
        }
    };

    const handleTableChange = (p: any) => {
        fetchCurrencies(
            p.current,
            p.pageSize,
            search,
            statusFilter
        );
    };

    const onSave = async () => {
        const values = await form.validateFields();

        try {
            if (selectedCurrency) {
                await updateCurrency(selectedCurrency.guid, values);
                message.success("Currency updated");
            } else {
                await createCurrency({
                    ...values,
                    createdBy: createdByGuid
                });
                message.success("Currency created");
            }

            setDrawerOpen(false);
            form.resetFields();
            fetchCurrencies(
                pagination.current,
                pagination.pageSize,
                search,
                statusFilter
            );
        } catch {
            message.error("Failed to save currency");
        }
    };

    const columns: ColumnsType<Currency> = [
        { title: "Name", dataIndex: "currencyName" },
        { title: "Code", dataIndex: "currencyCode" },
        { title: "Number", dataIndex: "currencyNumber" },
        { title: "Symbol", dataIndex: "currencySymbol" },
        {
            title: "Active",
            dataIndex: "active",
            render: (value, record) => (
                <Popconfirm
                    title={value ? "Disable currency" : "Enable currency"}
                    description={
                        value
                            ? "Are you sure you want to disable this currency?"
                            : "Are you sure you want to enable this currency?"
                    }
                    okText="Yes"
                    cancelText="No"
                    onConfirm={async () => {
                        try {
                            await updateCurrencyStatus(record.guid, {
                                active: !value
                            });

                            message.success("Status updated");

                            fetchCurrencies(
                                pagination.current,
                                pagination.pageSize,
                                search,
                                statusFilter
                            );
                        } catch {
                            message.error("Failed to update status");
                        }
                    }}
                    disabled={isReadOnly}
                >
                    <Switch
                        checked={value}
                        size="small"
                        onClick={(_, e) => e.preventDefault()}
                        disabled={isReadOnly}
                    />
                </Popconfirm>
            )
        },
        {
            title: "Actions",
            render: (_, record) => (
                <Tooltip title="Edit">
                    <Button
                        icon={
                            <EditOutlined
                                style={{ color: "var(--color-primary)" }}
                            />
                        }
                        onClick={() => {
                            setSelectedCurrency(record);
                            form.setFieldsValue(record);
                            setDrawerOpen(true);
                        }}
                    />
                </Tooltip>
            )
        }
    ];

    return (
        <div style={{ padding: 24 }}>
            <Title level={2}>Currencies</Title>

            <Space style={{ marginBottom: 16 }} wrap>
                <Search
                    placeholder="Search currencies"
                    onSearch={handleSearch}
                    onChange={handleSearchChange}
                    allowClear
                />

                <Segmented
                    value={statusFilter}
                    onChange={(val) =>
                        setStatusFilter(val as StatusFilter)
                    }
                    options={[
                        { label: "All", value: "all" },
                        { label: "Active", value: "active" },
                        { label: "Inactive", value: "inactive" }
                    ]}
                />

                <Button
                    color="primary"
                    variant="filled"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        setSelectedCurrency(null);
                        form.resetFields();
                        setDrawerOpen(true);
                    }}
                >
                    Add Currency
                </Button>
            </Space>

            {loading ? (
                <Skeleton active paragraph={{ rows: 6 }} />
            ) : (
                <Table
                    rowKey="guid"
                    dataSource={currencies}
                    columns={columns}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        pageSizeOptions: ["5", "10", "20", "50", "100"],
                        showTotal: (total, range) =>
                            `Showing ${range[0]}–${range[1]} of ${total} currencies`
                    }}
                    onChange={handleTableChange}
                />
            )}

            <CurrencyDrawer
                open={drawerOpen}
                editing={selectedCurrency}
                form={form}
                onClose={() => {
                    setDrawerOpen(false);
                    form.resetFields();
                }}
                onSave={onSave}
            />
        </div>
    );
};

export default Currencies;